import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CreditCard, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MockPaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  
  const checkoutId = searchParams.get("checkout_id");
  const amount = searchParams.get("amount") || "5000";

  const handleComplete = async (success) => {
    if (!checkoutId) {
      toast.error("No checkout ID found");
      return;
    }
    
    setIsProcessing(true);
    
    try {
      if (success) {
        // Mark payment as complete
        await axios.post(`${API}/payments/mock-complete/${checkoutId}`);
        toast.success("Payment simulated successfully!");
        
        // Get patient ID from session
        const patientData = sessionStorage.getItem("patientData");
        const patientId = patientData ? JSON.parse(patientData).patient_id : "";
        
        // Redirect to success page
        navigate(`/payment-success?patient_id=${patientId}&checkout_ref=${checkoutId}`);
      } else {
        toast.error("Payment cancelled");
        navigate("/payment");
      }
    } catch (error) {
      console.error("Mock payment error:", error);
      toast.error("Failed to process mock payment");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 py-12 px-4">
      <div className="max-w-md mx-auto">
        {/* Warning banner */}
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>Test Mode:</strong> This is a mock payment page for development testing.
          </p>
        </div>
        
        <Card className="shadow-lg" data-testid="mock-payment-card">
          <CardHeader className="text-center border-b bg-slate-50">
            <div className="flex justify-center mb-2">
              <CreditCard className="h-10 w-10 text-slate-600" />
            </div>
            <CardTitle className="text-xl">Mock Payment Gateway</CardTitle>
            <p className="text-sm text-slate-500 mt-1">SumUp Simulation</p>
          </CardHeader>
          
          <CardContent className="py-8">
            <div className="text-center mb-8">
              <p className="text-sm text-slate-500 mb-1">Amount to Pay</p>
              <p className="text-4xl font-bold text-slate-900">
                £{(parseInt(amount) / 100).toFixed(2)}
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-600">
                <strong>Checkout ID:</strong>
                <br />
                <code className="text-xs bg-slate-200 px-1 py-0.5 rounded">{checkoutId}</code>
              </p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={() => handleComplete(true)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={isProcessing}
                data-testid="btn-complete-payment"
              >
                {isProcessing ? (
                  <span className="spinner mr-2" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 mr-2" />
                )}
                Simulate Successful Payment
              </Button>
              
              <Button
                onClick={() => handleComplete(false)}
                variant="outline"
                className="w-full border-red-200 text-red-600 hover:bg-red-50"
                disabled={isProcessing}
                data-testid="btn-cancel-payment"
              >
                <XCircle className="h-5 w-5 mr-2" />
                Cancel Payment
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <p className="text-center text-xs text-slate-500 mt-6">
          In production, this page would be the actual SumUp hosted checkout.
        </p>
      </div>
    </div>
  );
}
